import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuShieldCheck, LuReceipt } from 'react-icons/lu';
import api from '../../services/api';
import { formatDateISO, getTodayISO, fechaAR } from '../../utils/dateUtils';
import ModalShell from '../ui/ModalShell';
import AgendaHuecos from './AgendaHuecos';

// Volver por un reclamo (28-oct-2026, backend: ordenes/reclamos.py). Sobre una visita ya hecha: qué reclaman,
// técnico y día (la misma agenda de siempre) y si va sin cargo. La app lo propone por la garantía (3 meses
// desde el trabajo); al cerrar, el técnico elige la causa y eso lo confirma o lo cambia.
const sumarDias = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return formatDateISO(d); };

export default function ReclamoSheet({ orden, onCerrar, onListo }) {
    const hoy = getTodayISO();
    const [info, setInfo] = useState(null);
    const [tecnicos, setTecnicos] = useState([]);
    const [ordenes, setOrdenes] = useState([]);
    const [motivo, setMotivo] = useState('');
    const [otro, setOtro] = useState('');
    const [detalle, setDetalle] = useState('');
    const [fecha, setFecha] = useState(hoy);
    const [hueco, setHueco] = useState(orden.tecnicoId ? { tecnicoId: orden.tecnicoId, franja: null } : null);
    const [cobro, setCobro] = useState(null);
    const [series, setSeries] = useState([]);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        api.get(`/ordenes/${orden.id}/reclamo`).then(r => { setInfo(r.data); setCobro(r.data.enGarantia ? 'GARANTIA' : 'COBRA');
            if ((r.data.equipos || []).length === 1) setSeries([r.data.equipos[0].serie]); })
            .catch(e => { toast.error(e?.response?.data?.mensaje || 'No se pudo abrir'); onCerrar(); });
        api.get('/ordenes/tecnicos').then(r => setTecnicos(r.data || [])).catch(() => {});
        api.get('/ordenes', { params: { desde: sumarDias(hoy, -7), hasta: sumarDias(hoy, 60) } })
            .then(r => setOrdenes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, [orden.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // 30-oct-2026: con "Otro" y lo escrito solo en el detalle, vale el detalle (antes frenaba con "Elegí qué reclaman")
    const primeraLinea = detalle.trim().split('\n')[0].slice(0, 120);
    const motivoFinal = motivo === 'Otro' || !motivo ? (otro.trim() || primeraLinea) : motivo;
    const equipos = info?.equipos || [];
    const varios = equipos.length > 1;
    const tocarSerie = (sr) => setSeries(a => a.includes(sr) ? a.filter(x => x !== sr) : [...a, sr]);
    const guardar = async () => {
        if (varios && series.length === 0) { toast.error('Elegí de qué equipo es el reclamo'); return; }
        if (!motivoFinal) { toast.error(motivo === 'Otro' ? 'Escribí qué pasa' : 'Elegí qué reclaman'); return; }
        if (!hueco?.franja) { toast.error('Tocá una franja libre en la agenda'); return; }
        setGuardando(true);
        try {
            await api.post(`/ordenes/${orden.id}/reclamo`, { motivo: motivoFinal, detalle: detalle.trim() === motivoFinal ? '' : detalle, series, tecnicoId: hueco.tecnicoId, fecha, hora: hueco.franja, cobro });
            toast.success(`Reclamo agendado${cobro === 'GARANTIA' ? ' · sin cargo' : ''}`);
            onListo?.();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo agendar'); }
        finally { setGuardando(false); }
    };
    const chip = (on) => `h-10 px-3.5 rounded-full text-label font-black border active:scale-95 ${on ? 'border-brand-red bg-[rgba(232,66,47,0.10)] text-ink' : 'border-black/10 dark:border-white/10 text-secondary'}`;

    return (
        <ModalShell titulo="Volver por un reclamo" ancho="md:max-w-2xl"
            subtitulo={[orden.lugar || orden.clienteNombre, String(orden.direccion || '').split(',')[0]].filter(Boolean).join(' · ')}
            onCerrar={onCerrar}
            pie={<button type="button" disabled={guardando || !info} onClick={guardar}
                className="w-full h-12 rounded-xl bg-[#C9341F] text-white font-black text-body active:scale-95 disabled:opacity-40">
                {guardando ? 'Agendando…' : 'Agendar el reclamo'}</button>}>
            {!info ? <p className="py-10 text-center text-muted">Cargando…</p> : (
                <div className="space-y-4">
                    <div className="rounded-2xl bg-panel p-3.5 space-y-1">
                        <p className="text-caption text-muted">Trabajo del {fechaAR(orden.fechaCompletada || orden.fechaProgramada)}</p>
                        {equipos.length === 0 && <p className="text-body font-bold text-ink">{info.hecho || orden.hecho || 'Sin detalle'}</p>}
                        {equipos.length === 1 && (
                            <p className="text-body font-bold text-ink"><span className="font-mono">{equipos[0].serie}</span>
                                {(equipos[0].hecho || info.hecho) && <span className="font-normal text-secondary"> · {equipos[0].hecho || info.hecho}</span>}</p>
                        )}
                        {info.garantiaHasta && (
                            <p className={`text-caption font-bold ${info.enGarantia ? 'text-[var(--etapa-listo-tx)]' : 'text-muted'}`}>
                                {info.enGarantia ? `En garantía hasta el ${fechaAR(info.garantiaHasta)}` : `Garantía vencida el ${fechaAR(info.garantiaHasta)}`}
                            </p>
                        )}
                    </div>

                    {varios && (
                        <div className="space-y-2">
                            <p className="px-1 text-caption font-bold text-muted">¿De qué equipo? En esa visita se hicieron {equipos.length}</p>
                            <div className="space-y-1.5">
                                {equipos.map(e => {
                                    const on = series.includes(e.serie);
                                    return (
                                        <button key={e.serie} type="button" onClick={() => tocarSerie(e.serie)} aria-pressed={on}
                                            className={`w-full p-3 rounded-2xl border-2 text-left active:scale-[0.99] ${on ? 'border-brand-red bg-[rgba(232,66,47,0.08)]' : 'border-black/10 dark:border-white/10'}`}>
                                            <span className="block text-body font-black text-ink font-mono">{on ? '✓ ' : ''}{e.serie}</span>
                                            <span className="block text-caption text-muted">{e.hecho || 'Sin detalle'}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        <p className="px-1 text-caption font-bold text-muted">¿Qué reclaman?</p>
                        <div className="flex flex-wrap gap-1.5">
                            {info.motivos.map(m => <button key={m} type="button" onClick={() => setMotivo(m)} aria-pressed={motivo === m} className={chip(motivo === m)}>{m}</button>)}
                        </div>
                        {motivo === 'Otro' && (
                            <input value={otro} onChange={e => setOtro(e.target.value)} maxLength={120} placeholder="Qué pasa"
                                className="w-full h-11 px-3 rounded-xl bg-page text-ink text-body outline-none border border-black/10 dark:border-white/10 placeholder:text-muted" />
                        )}
                        <textarea rows={2} value={detalle} onChange={e => setDetalle(e.target.value)} maxLength={500} placeholder="Detalle (opcional): pierde por la canilla de agua fría"
                            className="w-full px-3 py-2.5 rounded-xl bg-page text-ink text-body outline-none border border-black/10 dark:border-white/10 placeholder:text-muted resize-none" />
                    </div>

                    <div className="space-y-2">
                        <p className="px-1 text-caption font-bold text-muted">¿Se cobra?</p>
                        <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setCobro('GARANTIA')} aria-pressed={cobro === 'GARANTIA'}
                                className={`p-3 rounded-2xl border-2 text-left ${cobro === 'GARANTIA' ? 'border-[var(--etapa-listo)] bg-[rgba(22,163,74,0.10)]' : 'border-black/10 dark:border-white/10'}`}>
                                <span className="flex items-center gap-1.5 text-body font-black text-ink"><LuShieldCheck size={16} /> Sin cargo</span>
                                <span className="block text-caption text-muted">Garantía: no suma en el mes</span>
                            </button>
                            <button type="button" onClick={() => setCobro('COBRA')} aria-pressed={cobro === 'COBRA'}
                                className={`p-3 rounded-2xl border-2 text-left ${cobro === 'COBRA' ? 'border-brand-red bg-[rgba(232,66,47,0.08)]' : 'border-black/10 dark:border-white/10'}`}>
                                <span className="flex items-center gap-1.5 text-body font-black text-ink"><LuReceipt size={16} /> Se cobra</span>
                                <span className="block text-caption text-muted">Falla nueva o mal uso</span>
                            </button>
                        </div>
                        <p className="px-1 text-caption text-muted">Al cerrarlo, el técnico elige la causa y eso lo confirma (lo podés cambiar después).</p>
                    </div>

                    <div className="space-y-2">
                        <p className="px-1 text-caption font-bold text-muted">¿Quién va y cuándo?</p>
                        <AgendaHuecos tecnicos={tecnicos} ordenes={ordenes} fecha={fecha} onFecha={setFecha}
                            hueco={hueco} onHueco={setHueco} direccion={orden.direccion} />
                    </div>
                </div>
            )}
        </ModalShell>
    );
}
